import { useDispatch, useSelector } from "react-redux";
import React, { Fragment, useEffect, useState, useRef } from "react";
import { fetchLifestyle, fetchLifestyleTrends } from "../../../../../redux/modules/patient/actions";
import { useParams } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Menu, Transition } from "@headlessui/react";
import { ReactSVG } from "react-svg";
import { formatToYYYY_MMM_DD } from "../../../../../helpers/functions";
import PrescriptionTabs from "../../../clinical-services/service-steps/education/prescription-tabs";
const LifestyleTabs = PrescriptionTabs;
import {
  alcoholEnum,
  smokingEnum,
  exerciseEnum,
  recreationalDrugsEnum,
  dietEnum,
  caffeineEnum,
  exerciseTypeEnum,
} from "../../../clinical-services/service-steps/clinical-assessment/data";
import LifestyleFactorCreate from "./LifestyleFactorCreate";
import LifestyleHistoryTab from "./LifestyleHistoryTab";

// Define the static tabs outside component to avoid initialization issues
const lifestyleTabs = [
  { name: "dashboard", label: "Lifestyle Dashboard" },
  { name: "trends", label: "Lifestyle Trends" },
  { name: "history", label: "Lifestyle History" },
];

// Helper function to format lifestyle date
// Backend uses LastLifeStyleDate as primary source, with fallback to CreatedDate
const formatLifestyleDate = (lifestyle) => {
  // Prioritize lastLifeStyleDate (primary), then dateRecorded (for trends), then createdDate (fallback)
  const dateValue = lifestyle?.lastLifeStyleDate || lifestyle?.dateRecorded || lifestyle?.createdDate;
  
  if (!dateValue || (typeof dateValue === 'string' && dateValue.includes('0001-01-01'))) {
    return "No Date";
  }
  
  const dateString = dateValue instanceof Date ? dateValue.toISOString() : dateValue;
  const formatted = formatToYYYY_MMM_DD(dateString);
  
  return formatted || "No Date";
};

const LifestyleFactors = () => {
  const dispatch = useDispatch();
  const { id: patientId } = useParams();
  const { lifestyle, lifestyleTrends, patient, loading } = useSelector((state) => state.patients);
  // Watch clinical service updates to refresh lifestyle data
  const { clinicalServiceObj } = useSelector((state) => state.clinicalService);

  // Set default tab to "Lifestyle Trends"
  const [activeTab, setActiveTab] = useState(lifestyleTabs.find(t => t.name === 'trends') || lifestyleTabs[0]);
  const [lifeStyleFactors, setLifeStyleFactors] = useState([]);
  const [showCreateSlider, setShowCreateSlider] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [editingLifestyle, setEditingLifestyle] = useState(null);
  const lastClinicalServiceUpdateRef = useRef(null);

  // Fetch current lifestyle for dashboard
  useEffect(() => {
    dispatch(fetchLifestyle(patientId));
  }, [patientId, dispatch]);

  // Fetch trends when trends tab is active
  useEffect(() => {
    if (activeTab?.name === "trends") {
      dispatch(fetchLifestyleTrends(patientId));
    }
  }, [activeTab, patientId, dispatch]);

  // Watch for clinical service updates and refresh lifestyle data
  useEffect(() => {
    if (clinicalServiceObj?.initialAssessment?.initialAssessmentForm) {
      const clinicalAssessment = clinicalServiceObj.initialAssessment.initialAssessmentForm.clinicalAssessment;
      const healthAndWellness = clinicalServiceObj.initialAssessment.initialAssessmentForm.healthAndWellness;
      const lifestyleData = clinicalAssessment || healthAndWellness;
      
      if (lifestyleData) {
        // Create a unique key from lifestyle data to detect changes
        const lifestyleKey = JSON.stringify({
          alcohol: lifestyleData.alcohol,
          caffeine: lifestyleData.caffeine,
          smoking: lifestyleData.smoking,
          exercise: lifestyleData.exercise,
        });
        
        // Only refresh if the data has actually changed
        if (lifestyleKey !== lastClinicalServiceUpdateRef.current) {
          lastClinicalServiceUpdateRef.current = lifestyleKey;
          
          // Refresh lifestyle data after a short delay to allow backend processing
          const refreshTimer = setTimeout(() => {
            dispatch(fetchLifestyle(patientId));
            if (activeTab?.name === "trends") {
              dispatch(fetchLifestyleTrends(patientId));
            }
          }, 1000); // 1 second delay to allow backend to process
          
          return () => clearTimeout(refreshTimer);
        }
      }
    }
  }, [clinicalServiceObj, patientId, dispatch, activeTab]);

  // Update trends cards when trends data changes - limit to latest 6 cards and remove duplicates
  useEffect(() => {
    if (activeTab?.name !== "trends" || !lifestyleTrends || !Array.isArray(lifestyleTrends)) {
      setLifeStyleFactors([]);
      return;
    }

    // Helper function to normalize array fields for comparison
    const normalizeArrayField = (field) => {
      if (Array.isArray(field)) {
        return field.sort().join(',');
      }
      return field || '';
    };

    // Helper function to check if record has valid lifestyle data
    const hasLifestyleData = (item) => {
      return !!(
        item.alcohol ||
        item.caffeine ||
        item.smoking ||
        item.exercise ||
        item.recreationalDrugs ||
        item.exerciseTypes
      );
    };

    // Helper function to create data key for duplicate detection
    const createDataKey = (item) => {
      const dateValue = item.dateRecorded || item.lastLifeStyleDate || item.createdDate || '';
      const serviceType = item.serviceType || '';
      const alcohol = item.alcohol || '';
      const caffeine = item.caffeine || '';
      const smoking = item.smoking || '';
      const exercise = item.exercise || '';
      const recreationalDrugs = normalizeArrayField(item.recreationalDrugs);
      const exerciseTypes = normalizeArrayField(item.exerciseTypes);

      return `${dateValue}_${serviceType}_${alcohol}_${caffeine}_${smoking}_${exercise}_${recreationalDrugs}_${exerciseTypes}`;
    };

    // Helper function to get unique key for a record
    const getUniqueKey = (item, dataKey) => {
      if (item.id) return `id_${item.id}`;
      if (item.historyId) return `historyId_${item.historyId}`;
      return `combo_${dataKey}`;
    };

    // Helper function to get record date for sorting
    const getRecordDate = (item) => {
      return new Date(item.dateRecorded || item.lastLifeStyleDate || item.createdDate || 0);
    };

    const uniqueMap = new Map();
    const dataKeyMap = new Map();

    lifestyleTrends.forEach((item) => {
      // Skip records with no lifestyle data
      if (!hasLifestyleData(item)) {
        return;
      }

      const dataKey = createDataKey(item);
      const uniqueKey = getUniqueKey(item, dataKey);
      const existingDataRecord = dataKeyMap.get(dataKey);

      if (existingDataRecord) {
        // Duplicate detected - prefer current record over history, or more recent date
        const existingDate = getRecordDate(existingDataRecord);
        const currentDate = getRecordDate(item);

        // Determine if we should replace the existing record
        let shouldReplace = false;

        if (item.id && !existingDataRecord.id) {
          // New record is current, existing is history-only - replace it
          shouldReplace = true;
        } else if (!item.id && existingDataRecord.id) {
          // Existing is current, new is history-only - skip the new one
          shouldReplace = false;
        } else {
          // Both are same type (both current or both history) - prefer more recent date
          shouldReplace = currentDate > existingDate;
        }

        if (shouldReplace) {
          uniqueMap.delete(existingDataRecord.uniqueKey);
          uniqueMap.set(uniqueKey, item);
          dataKeyMap.set(dataKey, { ...item, uniqueKey });
        }
        // If shouldReplace is false, skip adding this duplicate
      } else {
        // First occurrence - add to maps
        uniqueMap.set(uniqueKey, item);
        dataKeyMap.set(dataKey, { ...item, uniqueKey });
      }
    });

    // Sort by date (newest first) and limit to 6 cards
    const uniqueTrends = Array.from(uniqueMap.values())
      .sort((a, b) => getRecordDate(b) - getRecordDate(a))
      .slice(0, 6);

    setLifeStyleFactors(uniqueTrends);
  }, [lifestyleTrends, activeTab]);


  const renderTabContent = () => {
    switch (activeTab?.name) {
      case "dashboard":
        return (
          <div className="w-full text-2xl font-bold text-gray-800 gap-5 h-full flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-teal-600 mb-4">
                Lifestyle Dashboard
              </h2>
              <p className="text-lg text-gray-600">Coming Soon</p>
            </div>
          </div>
        );
      case "trends":
        return (
          <div className="w-full h-full overflow-x-auto">
            {loading && (!lifeStyleFactors || lifeStyleFactors.length === 0) ? (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 border-t-transparent border-r-transparent"></div>
                  <p className="mt-4 text-gray-600">Loading lifestyle trends...</p>
                </div>
              </div>
            ) : (
              <div className="w-full relative min-w-[400px]">
                <div
                  className="flex gap-[30px] overflow-x-auto h-full min-h-[600px] scroll-smooth px-0"
                  id="insurance-information"
                >
                  {/* Add Lifestyle Factors Card - Always on Left */}
                  <div className="w-[400px] min-w-[400px] min-h-[230px] max-h-[570px] flex-shrink-0">
                    <button
                      onClick={() => {
                        // Always open with empty form for creating new lifestyle
                        setShowCreateSlider(true);
                      }}
                      className="inline-flex justify-center items-center flex-col w-full h-full p-4 align-top bg-white border-2 border-dashed border-REELTeal rounded text-lg font-medium text-REELTeal"
                    >
                      <PlusIcon className="w-6 h-6 text-REELTeal" />
                      <span className="font-bold text-REELTeal text-body-md">
                        Add Lifestyle Factors
                      </span>
                    </button>
                  </div>

                  {/* Lifestyle Data Cards - Show on Right, Newest First */}
                  {lifeStyleFactors && lifeStyleFactors.length > 0 && (
                  <>
                    {lifeStyleFactors.map((lifestyle, index) => (
                      <div key={lifestyle.historyId || lifestyle.id || index} className="mainCard max-h-[570px] flex-shrink-0 w-[400px] min-w-[400px]" style={{ marginLeft: 0, marginRight: 0 }}>
                        <div className={`h-[72px] flex items-center justify-between px-6 py-3 border-b ${index === 0 ? 'bg-REELTeal text-white' : 'border-gray-100 bg-gray-50'}`}>
                          <div className="flex flex-col">
                            <h4 className={`flex items-center text-lg font-bold ${index === 0 ? 'text-white' : 'text-teal-600'}`}>
                              {formatLifestyleDate(lifestyle)}
                            </h4>
                            
                          </div>

                          {/* Only show edit button for the card with Patient Chart source */}
                          {index === 0 && lifestyle?.serviceType === "Patient Chart" && (
                            <div>
                              <Menu as="div" className="relative inline-block text-left">
                                <div>
                                  <Menu.Button>
                                    <ReactSVG
                                      src="/icons/triple-dots-white.svg"
                                      className="flex text-black !items-center w-6 scale-x-[2] scale-y-[2]"
                                    />
                                  </Menu.Button>
                                </div>
                                <Transition
                                  as={Fragment}
                                  enter="transition ease-out duration-100"
                                  enterFrom="transform translate-y-2 opacity-0 scale-95"
                                  enterTo="transform translate-y-0 opacity-100 scale-100"
                                  leave="transition ease-in duration-75"
                                  leaveFrom="transform translate-y-0 opacity-100 scale-100"
                                  leaveTo="transform translate-y-2 opacity-0 scale-95"
                                >
                                  <Menu.Items className="absolute top-full left-1/2 -translate-x-1/2 mt-1 min-w-[80px] w-auto origin-top bg-white text-black  ring-1 ring-black ring-opacity-5">
                                    <Menu.Item>
                                    <button
                                      className="block text-left font-medium w-[80px] h-[38px] px-[10px] py-[10px] flex items-center gap-[10px] hover:bg-gray-50"
                                      onClick={() => {
                                        // Pass the current lifestyle to the edit form
                                        setEditingLifestyle(lifestyle);
                                        setShowCreateSlider(true);
                                      }}
                                    >
                                      Edit
                                    </button>
                                    </Menu.Item>
                                  </Menu.Items>
                                </Transition>
                              </Menu>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center justify-between px-6 py-2 bg-teal-25 border-b border-gray-100">
                          <span className={`text-[18px] mt-1 text-teal-300`}>
                            Source: {lifestyle.serviceType }
                          </span>
                        </div>
                        <div className="px-6 py-3.5 h-[87%] overflow-y-auto">
                          <div className="grid grid-cols-1">
                            <div className="break-words">
                              <p className="mb-1.5 text-sm text-gray-700">
                                Alcohol
                              </p>
                              <p className="font-bold text-gray-800 capitalize text-body-md break-words">
                                {(() => {
                                  const alcoholOption = alcoholEnum.options.find(
                                    (x) => x.value === lifestyle.alcohol
                                  );
                                  return alcoholOption?.subHeader
                                    ? `${alcoholOption.header} - ${alcoholOption.subHeader}`
                                    : alcoholOption?.header || "";
                                })()}
                              </p>
                            </div>
                            <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                            <div>
                              <p className="mb-1.5 text-sm text-gray-700">
                                Caffeine
                              </p>
                              <p className="font-bold text-gray-800 text-body-md">
                                {(() => {
                                  const caffeineOption = caffeineEnum.options.find(
                                    (x) => x.value === lifestyle.caffeine
                                  );
                                  return caffeineOption?.header || "";
                                })()}
                              </p>
                            </div>
                            <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                            <div>
                              <p className="mb-1.5 text-sm text-gray-700">
                                Smoking
                              </p>
                              <p className="font-bold text-gray-800 text-body-md">
                                {(() => {
                                  const smokingOption = smokingEnum.options.find(
                                    (x) => x.value === lifestyle.smoking
                                  );
                                  return smokingOption?.header || "";
                                })()}
                              </p>
                            </div>
                            <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                            <div>
                              <p className="mb-1.5 text-sm text-gray-700">
                                Recreational Drugs
                              </p>
                              <p className="font-bold text-gray-800 text-body-md">
                                {(() => {
                                  if (!lifestyle.recreationalDrugs) {
                                    return 'None';
                                  }
                                  // Handle both string (comma-separated) and array formats
                                  const drugs = typeof lifestyle.recreationalDrugs === 'string' 
                                    ? lifestyle.recreationalDrugs.split(',').map(d => d.trim())
                                    : Array.isArray(lifestyle.recreationalDrugs) 
                                      ? lifestyle.recreationalDrugs 
                                      : [];
                                  
                                  if (drugs.length === 0) return 'None';
                                  
                                  return drugs
                                    .map((drug) => {
                                      // Check if this is "Other" and replace with custom text
                                      if (drug?.toLowerCase() === "other" || drug === "Other") {
                                        return lifestyle.recreationalDrugsOther || "Other";
                                      }
                                      const drugOption = recreationalDrugsEnum.options.find(
                                        (x) => x.value === drug
                                      );
                                      return drugOption?.header || drug;
                                    })
                                    .join(", ");
                                })()}
                              </p>
                            </div>
                            <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                            <div>
                              <p className="mb-1.5 text-sm text-gray-700">
                                Exercise
                              </p>
                              <p className="font-bold text-gray-800 text-body-md">
                                {(() => {
                                  const exerciseOption = exerciseEnum.options.find(
                                    (x) => x.value === lifestyle.exercise
                                  );
                                  return exerciseOption?.header || "";
                                })()}
                              </p>
                            </div>
                            {/* Show Exercise Types only if they exist as a separate field */}
                            {lifestyle.exercise && lifestyle.exercise !== "Never" && lifestyle.exerciseTypes && (
                              (() => {
                                // Handle both array and string formats
                                const exerciseTypes = Array.isArray(lifestyle.exerciseTypes)
                                  ? lifestyle.exerciseTypes
                                  : typeof lifestyle.exerciseTypes === 'string'
                                    ? lifestyle.exerciseTypes.split(',').map(t => t.trim()).filter(t => t)
                                    : [];
                                
                                // Only show if we have exercise types
                                if (exerciseTypes.length === 0) return null;
                                
                                return (
                                  <>
                                    <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                                    <div>
                                      <p className="mb-1.5 text-sm text-gray-700">
                                        Exercise Types
                                      </p>
                                      <p className="font-bold text-gray-800 text-body-md">
                                        {exerciseTypes
                                          .map((type) => {
                                            const typeOption = exerciseTypeEnum.options.find(
                                              (x) => x.value === type
                                            );
                                            return typeOption?.header || type;
                                          })
                                          .join(", ")}
                                      </p>
                                    </div>
                                  </>
                                );
                              })()
                            )}
                            <div className="border-b border-b-gray-100 mb-3 pt-2.5"></div>
                            <div>
                              <p className="mb-1.5 text-sm text-gray-700">
                                Comments
                              </p>
                              <p className="font-bold text-gray-800 text-body-md">
                                {(() => {
                                  // Backend now separates ExerciseTypes from Comments
                                  // Comments field only contains recreational drug comments (RecreationalDrugsComments)
                                  // ExerciseTypes are shown separately above
                                  return lifestyle.comments || "";
                                })()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                  )}
                </div>

                {/* Scroll Arrows */}
                {isOverflowing && (
                <>
                  {enableLeftArrow && (
                    <button
                      className="absolute m-auto left-0 bg-gray-20 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold"
                      onClick={handleLeftScroll}
                    >
                      <ReactSVG
                        src="/icons/arrow-right.svg"
                        className="w-[15px] rotate-180"
                      />
                    </button>
                  )}
                  {enableRightArrow && (
                    <button
                      className="absolute m-auto right-0 bg-gray-20 px-3 py-[18px] top-0 bottom-0 h-fit text-teal-600 font-bold"
                      onClick={handleRightScroll}
                    >
                      <ReactSVG
                        src="/icons/arrow-right.svg"
                        className="w-[15px]"
                      />
                    </button>
                  )}
                </>
                )}
              </div>
            )}
          </div>
        );
      case "history":
        return (
          <LifestyleHistoryTab patientId={patientId} />
        );
      default:
        return (
          <div className="w-full text-2xl font-bold text-gray-800 gap-5 h-full flex items-center justify-center">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-teal-600 mb-4">
                Lifestyle Dashboard
              </h2>
              <p className="text-lg text-gray-600">Coming Soon</p>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col flex-1 max-h-[802px] gap-[10px] w-full overflow-hidden">
      {/* Header - Above Tabs */}
      <div className="flex justify-between items-center pb-[10px] border-b-[7px] border-gray-20">
        <div className="flex gap-3 items-center">
          <h4 className="text-[22px] font-bold justify-center text-teal-300 px-[5px]">
            Lifestyle Factors
          </h4>
        </div>
      </div>

      {/* Header Tabs */}
      <LifestyleTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={lifestyleTabs}
      />

      {/* Tab Content */}
      <div className="flex-1 bg-white overflow-y-auto">
        {renderTabContent()}
      </div>

      {/* Lifestyle Create Slider */}
      <LifestyleFactorCreate
        open={showCreateSlider}
        onClose={() => {
          setShowCreateSlider(false);
          setEditingLifestyle(null);
        }}
        selectedLifestyle={editingLifestyle || null}
        patientId={patientId}
        create={!editingLifestyle}
        onSave={() => {
          // This is called after successful save
        }}
        onSuccess={() => {
          setShowCreateSlider(false);
          setEditingLifestyle(null);
          // Add a small delay to ensure backend has processed the request
          setTimeout(() => {
            dispatch(fetchLifestyle(patientId));
            // Refresh trends if trends tab is active
            if (activeTab?.name === "trends") {
              dispatch(fetchLifestyleTrends(patientId));
            }
          }, 500); // 500ms delay
        }}
      />
    </div>
  );
};

export default LifestyleFactors;
